/**
 * Pinned "Me" — not one of the FSD's numbered parts (docs §19161): the
 * self-service screens visible to essentially every signed-in user
 * regardless of job. Two sections with no `kind` (they are not FSD
 * sections): `self` — the ESS "My …" screens of FSD 9.2.8 (HCM-F08), plus
 * one's own timesheet week (12.2.4, PRJ-F04) and service-charge share
 * (catalogue requirement HCM-FR-019) — and `account`, Profile & security.
 *
 * Item ids are stable: favourites and recents are keyed off `id` in
 * localStorage, not off section or position.
 */
import type { NavGroup } from '../types'
import { ESS_PERMISSIONS } from '@/modules/ess/permissions'
import { ESS_C10_PERMISSIONS } from '@/modules/ess/permissionsC10'
import { PROJECTS_PERMISSIONS } from '@/modules/projects/permissions'

export const ME_GROUPS: NavGroup[] = [
  // Self-service (§8.12's ESS, §8.13's timesheet grid) — second from the top,
  // and its own group rather than a corner of another one.
  //
  // Every other group in this sidebar is a job somebody does. This one is the
  // only surface a person with no job in the system at all still has, and its
  // permissions — `hr.employee.self.read`, `payroll.payslip.read`,
  // `hr.leave.read` — are held by the seeded Employee role, which is to say by
  // essentially everybody. Two placements were considered and rejected:
  //
  //  - Inside `people`, beside Employees and Payroll runs. That files a payslip
  //    under HR administration. Most people who can see "My payslips" can see
  //    nothing else in that group, so for them the group would render as one
  //    lonely item under a heading naming a department they do not work in —
  //    and for an HR manager, "My payslips" would sit inches from the list of
  //    everybody's, which is precisely the confusion §8.12 designed the
  //    self-scoped permissions to prevent.
  //  - Inside `workspace`, beside Dashboard and Approvals. Closer, but that
  //    group is the day's shared work — what is waiting for you from other
  //    people — and five personal records would outnumber and dilute it.
  //
  // Near the top because for the majority of users it is the only group they
  // can open; below it they would scroll past fifteen headings of ledgers they
  // will never see to reach their own payslip.
  //
  // Every label here reads "My …". The distinction is load-bearing and is the
  // same one `projects-timesheets` — fourth in this group — was already making
  // on its own: these screens resolve the employee from the JWT and cannot be
  // pointed at a colleague, so a bare "Payslips" or "Leave" would advertise a
  // manager's list that does not exist at these URLs.
  {
    id: 'self',
    labelKey: 'nav.groups.self',
    items: [
      {
        id: 'ess-profile',
        labelKey: 'ess.routeTitles.profile',
        to: '/me',
        icon: 'user',
        permission: ESS_PERMISSIONS.selfRead,
        keywordsKey: 'nav.items.essProfile.keywords',
        descriptionKey: 'nav.items.essProfile.description',
        fsd: '9.2.8',
        code: 'HCM-F08',
      },
      {
        id: 'ess-payslips',
        labelKey: 'ess.routeTitles.payslips',
        to: '/me/payslips',
        icon: 'download',
        permission: ESS_PERMISSIONS.payslipRead,
        keywordsKey: 'nav.items.essPayslips.keywords',
        descriptionKey: 'nav.items.essPayslips.description',
        fsd: '9.2.8',
        code: 'HCM-F08',
      },
      {
        id: 'ess-leave',
        labelKey: 'ess.routeTitles.leave',
        to: '/me/leave',
        icon: 'inbox',
        permission: ESS_PERMISSIONS.leaveRead,
        keywordsKey: 'nav.items.essLeave.keywords',
        descriptionKey: 'nav.items.essLeave.description',
        fsd: '9.2.8',
        code: 'HCM-F08',
      },
      {
        // Moved here from `delivery`, where it was the only self-service item in
        // a group of project and service administration. Its own comment already
        // argued it is one's own week rather than everybody's hours; grouping it
        // with the other five "My …" screens is that argument carried through.
        // The id is unchanged, because it is the localStorage key a user's
        // favourites and recents are stored under.
        //
        // The route declares `notification.read` — held by every role including
        // a bare Employee — because the server's registry has no "any signed-in
        // user" option and ownership is enforced by `requireTimesheetOwner`. So
        // this link is visible to essentially everybody, and "Timesheets" beside
        // it would read as the manager's list of everyone's hours. It is one's
        // own week, and it says so.
        id: 'projects-timesheets',
        labelKey: 'nav.items.projectsTimesheets.label',
        to: '/projects/timesheets',
        icon: 'clock',
        permission: PROJECTS_PERMISSIONS.timesheetSelfService,
        keywordsKey: 'nav.items.projectsTimesheets.keywords',
        descriptionKey: 'nav.items.projectsTimesheets.description',
        fsd: '12.2.4',
        code: 'PRJ-F04',
      },
      {
        id: 'ess-attendance',
        labelKey: 'ess.routeTitles.attendance',
        to: '/me/attendance',
        icon: 'check',
        permission: ESS_PERMISSIONS.attendanceRead,
        keywordsKey: 'nav.items.essAttendance.keywords',
        descriptionKey: 'nav.items.essAttendance.description',
        fsd: '9.2.8',
        code: 'HCM-F08',
      },
      {
        id: 'ess-documents',
        labelKey: 'ess.routeTitles.documents',
        to: '/me/documents',
        icon: 'panelRight',
        permission: ESS_PERMISSIONS.selfDocuments,
        keywordsKey: 'nav.items.essDocuments.keywords',
        descriptionKey: 'nav.items.essDocuments.description',
        fsd: '9.2.8',
        code: 'HCM-F08',
      },
      {
        id: 'ess-roster',
        labelKey: 'ess.routeTitles.roster',
        to: '/me/roster',
        icon: 'calendar',
        permission: ESS_C10_PERMISSIONS.rosterReadOwn,
        fsd: '9.2.8',
        code: 'HCM-F08',
      },
      {
        id: 'ess-employment',
        labelKey: 'ess.routeTitles.employment',
        to: '/me/employment',
        icon: 'building',
        permission: ESS_C10_PERMISSIONS.selfAssignments,
        fsd: '9.2.8',
        code: 'HCM-F08',
      },
      {
        id: 'ess-service-charge',
        labelKey: 'ess.routeTitles.serviceCharge',
        to: '/me/service-charge',
        icon: 'scale',
        permission: ESS_C10_PERMISSIONS.serviceChargeSelf,
        // Service charge is specified only as Part 9 catalogue requirement
        // HCM-FR-019 (no 9.2.x feature spec).
        fsd: '9.1',
        code: 'HCM-FR-019',
      },
      {
        id: 'ess-year-end',
        labelKey: 'ess.routeTitles.yearEnd',
        to: '/me/year-end',
        icon: 'inbox',
        permission: ESS_C10_PERMISSIONS.yearEndSelf,
        fsd: '9.2.8',
        code: 'HCM-F08',
      },
    ],
  },
  {
    id: 'account',
    labelKey: 'nav.groups.account',
    items: [
      {
        id: 'profile',
        labelKey: 'nav.items.profile.label',
        to: '/profile',
        icon: 'user',
        keywordsKey: 'nav.items.profile.keywords',
        descriptionKey: 'nav.items.profile.description',
        fsd: 'D.5',
        // recorded deviation: holds password and MFA enrolment, DECISIONS.md C17
        fsdStatus: 'extraneous',
      },
    ],
  },
]
